import { ChevronDown, Info } from "lucide-react";
import { Fragment, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { activeFilterCount } from "@/lib/filters";
import type { FilterValues } from "@/lib/filters";

export interface FilterSpec {
  name: string;
  label: string;
  kind: "text" | "choice" | "multi";
  /** Required for `choice`; ignored for `text`. */
  /** `description` (segmented layout) shows an info mark and the text on hover. */
  options?: Array<{ value: string; label: string; description?: string }>;
  /**
   * What "no choice made" is called on THIS filter. "Any" reads right on a
   * status; "All" reads right on a set the reader thinks of as complete. The
   * module names it, because only the module knows which of the two it is.
   */
  anyLabel?: string;
  /**
   * Render the control inert. Used when the option list could not be loaded: a
   * silently-wrong option list is worse than a visibly-absent one, so the pill
   * stays on screen, greyed, and the module says why beside it.
   */
  disabled?: boolean;
}

interface FilterBarProps {
  filters: FilterSpec[];
  /**
   * Read from the URL by the module via `parseFilters` — never local state.
   * It may hold keys carried from another tab or pane; only the names in
   * `filters` and `clears` are counted, because Clear undoes only those.
   */
  values: FilterValues;
  onChange: (next: FilterValues) => void;
  /**
   * `stacked` gives each control its own labelled column — the roomy form
   * rhythm. `inline` is one quiet row of pills that reads "Domain: All", for a
   * bar that sits above a list and must not out-shout it. `segmented` renders
   * each `choice` filter as Meteoric's chip group (overline label, no Clear
   * button, no "Any" option) — for a control that always has a value, like a
   * time range.
   */
  layout?: "stacked" | "inline" | "segmented";
  /**
   * Pushed to the far end of the same row — the place for the count of what
   * the filters left behind, so "7 facts" sits with the controls that made it
   * 7 rather than on a line of its own.
   */
  trailing?: ReactNode;
  /**
   * `inline` only: a control the module owns, placed directly after the last
   * filter pill — a search box that narrows the same view. It is not one of the
   * bar's filters, so it does not count toward "Clear N filters".
   */
  afterFilters?: ReactNode;
  /**
   * `inline` only: module-owned controls placed directly after the named
   * filter pill (a search box beside the list it narrows). Like
   * `afterFilters`, they are not the bar's filters; list a URL key in `clears`
   * for Clear to empty it too.
   */
  beside?: Record<string, ReactNode>;
  /**
   * The "not really a filter" value for any filter that always carries a
   * value — a period picker's own default period. A filter sitting at its
   * default does not count toward "Clear N filters" and does not, on its
   * own, make the button appear (cvce-pipeline-2 fix: the week picker's
   * default week was showing "Clear 1 filter" with nothing to clear).
   */
  defaultValues?: FilterValues;
  /**
   * Keys of controls the module renders beside the bar (in `trailing`, say a
   * SKU picker or a date range) that Clear also resets. They count toward
   * "Clear N filters" like the bar's own filters, so the number always
   * matches what one click undoes.
   */
  clears?: string[];
  /**
   * `inline` only: each choice pill opens a list with a type-to-search box above "All", instead of the
   * browser's plain dropdown. Opt-in, so every other module keeps its native select.
   */
  searchable?: boolean;
}

/**
 * The shared filter controls (C3, I-7).
 *
 * Controlled on purpose. The values live in the URL — the module reads them
 * with `parseFilters` and writes them with `applyFilters` — so a filtered view
 * is a link a person can paste to a teammate. Holding filter state inside this
 * component would make every filtered screen unshareable, which is how "look
 * at this" turns into a paragraph of instructions.
 */
export function FilterBar({
  filters,
  values,
  onChange,
  layout = "stacked",
  trailing,
  afterFilters,
  beside,
  defaultValues,
  clears = [],
  searchable = false,
}: FilterBarProps) {
  // Count only what Clear undoes (the bar's own filters and `clears`): a key carried from another tab or pane is not this bar's.
  const names = [...filters.map((f) => f.name), ...clears];
  const active = activeFilterCount(Object.fromEntries(names.map((name) => [name, values[name]])), defaultValues);
  const clear = () => onChange(Object.fromEntries(names.map((name) => [name, null])));

  if (layout === "segmented") {
    return (
      <div role="search" className="flex flex-wrap items-end gap-4">
        {filters.map((filter) =>
          filter.kind === "multi" ? (
            <MultiFilter
              key={filter.name}
              filter={filter}
              value={listValue(values[filter.name])}
              onChange={(next) => onChange({ ...values, [filter.name]: next.length ? next : null })}
            />
          ) : filter.kind === "choice" ? (
            <SegmentedFilter
              key={filter.name}
              filter={filter}
              value={(values[filter.name] as string) ?? ""}
              onChange={(next) => onChange({ ...values, [filter.name]: next })}
            />
          ) : (
            <InlineFilter
              key={filter.name}
              filter={filter}
              value={(values[filter.name] as string) ?? ""}
              onChange={(next) => onChange({ ...values, [filter.name]: next || null })}
            />
          ),
        )}
        {trailing && <span className="text-[13px] text-ks-grey-1">{trailing}</span>}
      </div>
    );
  }

  if (layout === "inline") {
    return (
      <div role="search" className="flex flex-wrap items-center gap-2.5">
        {filters.map((filter) => (
          <Fragment key={filter.name}>
            {filter.kind === "multi" ? (
              <MultiFilter
                filter={filter}
                value={listValue(values[filter.name])}
                onChange={(next) => onChange({ ...values, [filter.name]: next.length ? next : null })}
              />
            ) : searchable && filter.kind === "choice" ? (
              <SearchableFilter
                filter={filter}
                value={(values[filter.name] as string) ?? ""}
                onChange={(next) => onChange({ ...values, [filter.name]: next || null })}
              />
            ) : (
              <InlineFilter
                filter={filter}
                value={(values[filter.name] as string) ?? ""}
                onChange={(next) => onChange({ ...values, [filter.name]: next || null })}
              />
            )}
            {beside?.[filter.name]}
          </Fragment>
        ))}

        {afterFilters}

        {active > 0 && (
          <button
            type="button"
            data-nonmutating
            onClick={clear}
            className="inline-flex h-8 items-center rounded-ks-sm border border-ks-purple-3 bg-ks-surface px-3 text-[13px] text-ks-purple"
          >
            Clear {active} filter{active === 1 ? "" : "s"}
          </button>
        )}

        {trailing && <span className="ml-auto text-[13px] text-ks-grey-1">{trailing}</span>}
      </div>
    );
  }

  return (
    <div role="search" className="flex flex-wrap items-end gap-3 py-2">
      {filters.map((filter) => (
        <label key={filter.name} className="flex flex-col gap-1 text-sm text-ks-ink">
          {filter.label}
          {filter.kind === "multi" ? (
            <MultiFilter
              filter={filter}
              value={listValue(values[filter.name])}
              onChange={(next) => onChange({ ...values, [filter.name]: next.length ? next : null })}
            />
          ) : filter.kind === "choice" ? (
            <select
              className="min-h-11 rounded-ks-sm border border-ks-grey-3 bg-ks-surface px-2 text-ks-ink"
              value={(values[filter.name] as string) ?? ""}
              onChange={(e) => onChange({ ...values, [filter.name]: e.target.value || null })}
            >
              <option value="">{filter.anyLabel ?? "Any"}</option>
              {(filter.options ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <Input
              className="min-h-11"
              value={(values[filter.name] as string) ?? ""}
              onChange={(e) => onChange({ ...values, [filter.name]: e.target.value || null })}
            />
          )}
        </label>
      ))}

      {active > 0 && (
        // Named with its count: "Clear" alone hides how much is being undone.
        <Button variant="outline" onClick={clear}>
          Clear {active} filter{active === 1 ? "" : "s"}
        </Button>
      )}

      {trailing && <span className="self-center text-sm text-ks-grey-1">{trailing}</span>}
    </div>
  );
}

const listValue = (value: FilterValues[string]): string[] => Array.isArray(value) ? value : value ? [value] : [];

export function toggleMultiValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function MultiFilter({ filter, value, onChange }: {
  filter: FilterSpec;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = value.length === 1 ? (filter.options ?? []).find((option) => option.value === value[0])?.label : undefined;
  const caption = value.length === 0 ? filter.anyLabel ?? "All" : value.length === 1 ? selected ?? value[0] : `${value.length} selected`;
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      <button
        type="button"
        role="combobox"
        aria-label={filter.label}
        aria-expanded={open}
        data-nonmutating
        disabled={filter.disabled}
        className="inline-flex h-8 items-center gap-1.5 rounded-ks-sm border border-ks-grey-4 bg-ks-surface px-3 text-[13px] text-ks-ink"
      >
        <span className="text-ks-grey-1">{filter.label}:</span>
        <span className="text-ks-ink">{caption}</span>
        <ChevronDown aria-hidden className="size-3 shrink-0 text-ks-grey-1" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-64 p-0" align="start">
      <Command><CommandList>{(filter.options ?? []).map((option) => <CommandItem
        key={option.value}
        value={`${option.label} ${option.value}`}
        onSelect={() => onChange(toggleMultiValue(value, option.value))}
      >
        <Checkbox
          data-nonmutating
          checked={value.includes(option.value)}
          tabIndex={-1}
          aria-label={option.label}
          className="pointer-events-none"
        />
        {option.label}
      </CommandItem>)}</CommandList></Command>
    </PopoverContent>
  </Popover>;
}

/**
 * One segmented chip group (ported from Meteoric's `SegmentedNavFilter`,
 * W2). A segmented filter always has a value — the module passes the
 * effective default — so there is no Clear button and no "Any" option.
 */
function SegmentedFilter({
  filter,
  value,
  onChange,
}: {
  filter: FilterSpec;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {filter.label}
      </span>
      <TooltipProvider>
      <div
        role="group"
        aria-label={filter.label}
        className="inline-flex h-9 overflow-hidden rounded-ks-sm border border-border bg-ks-surface"
      >
        {(filter.options ?? []).map((option, index) => {
          const active = option.value === value;
          const button = (
            <button
              key={option.value}
              type="button"
              data-nonmutating
              aria-pressed={active}
              aria-description={option.description}
              onClick={() => onChange(option.value)}
              className={[
                "inline-flex items-center gap-1.5 px-3.5 text-[13px] transition-colors",
                index > 0 ? "border-l border-border" : "",
                active
                  ? "bg-ks-purple-4 font-semibold text-ks-purple"
                  : "font-medium text-muted-foreground hover:bg-muted",
              ].join(" ")}
            >
              {option.label}
              {option.description && <Info aria-hidden className="size-3.5 opacity-60" />}
            </button>
          );
          if (!option.description) return button;
          return (
            <Tooltip key={option.value}>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent>{option.description}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      </TooltipProvider>
    </div>
  );
}

/**
 * One pill. The label stays VISIBLE — "Domain: All", not a bare "All" the
 * reader has to infer from column order — and it is the control's own `label`
 * element, so the name a screen reader announces and the name on screen are the
 * same string.
 */
function InlineFilter({
  filter,
  value,
  onChange,
}: {
  filter: FilterSpec;
  value: string;
  onChange: (next: string) => void;
}) {
  const shell =
    "inline-flex h-8 items-center gap-1.5 rounded-ks-sm border border-ks-grey-4 bg-ks-surface px-3 text-[13px] text-ks-ink";

  if (filter.kind === "text") {
    return (
      <label className={shell}>
        <span className="text-ks-grey-1">{filter.label}:</span>
        <input
          data-nonmutating
          className="w-28 bg-transparent text-[13px] text-ks-ink outline-none"
          value={value}
          disabled={filter.disabled}
          placeholder={filter.anyLabel ?? "Any"}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  }

  // `data-nonmutating` on the select below is load-bearing, not decoration.
  // PermissionGate makes every `select` it does not see that marker on
  // `pointer-events: none` for a read-only viewer. Because this select is
  // stretched over the whole pill, an unmarked one takes the chevron and the
  // label down with it and the entire filter goes dead -- which is what a
  // view-only operator reported on the Distribution Leads and Opportunities
  // tabs. Narrowing a view is not an edit, so the marker belongs here.
  //
  // The select is STRETCHED over the whole pill rather than sized to its value
  // text, and the value is rendered as ordinary text beneath it. The old layout
  // drew the chevron as a SIBLING of the select, so the arrow - the one part of
  // a dropdown everybody aims at - sat outside the control's hit area and a
  // click on it only focused the select without opening its popup (bug 815f).
  const selectedLabel = value ? (filter.options ?? []).find((o) => o.value === value)?.label : undefined;
  return (
    <label className={`${shell} relative`}>
      <span className="text-ks-grey-1">{filter.label}:</span>
      <span className="text-ks-ink">{selectedLabel ?? filter.anyLabel ?? "Any"}</span>
      {/* Decorative only - the real control is the transparent select above it,
          so a screen reader must not announce a second thing to click. */}
      <ChevronDown aria-hidden className="size-3 shrink-0 text-ks-grey-1" />
      <select
        data-nonmutating
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        value={value}
        disabled={filter.disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{filter.anyLabel ?? "Any"}</option>
        {(filter.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * One searchable pill: the same "Label: value" face as `InlineFilter`, opening a list whose first row is a
 * type-to-search box, then "All", then the options. Typing narrows the list by label. The trigger and the box
 * are marked `data-nonmutating` for the same reason as the select above: narrowing a view is not an edit.
 */
function SearchableFilter({
  filter,
  value,
  onChange,
}: {
  filter: FilterSpec;
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const anyLabel = filter.anyLabel ?? "Any";
  const selectedLabel = value ? (filter.options ?? []).find((o) => o.value === value)?.label : undefined;
  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-nonmutating
          role="combobox"
          aria-expanded={open}
          aria-label={filter.label}
          disabled={filter.disabled}
          className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-ks-sm border border-ks-grey-4 bg-ks-surface px-3 text-[13px] text-ks-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="text-ks-grey-1">{filter.label}:</span>
          <span className="text-ks-ink">{selectedLabel ?? anyLabel}</span>
          <ChevronDown aria-hidden className="size-3 shrink-0 text-ks-grey-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput data-nonmutating placeholder="Type to search" />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {/* "All" stays listed whatever is typed, so clearing the choice is always one click. */}
              <CommandItem value={`__any__ ${anyLabel}`} forceMount onSelect={() => pick("")}>
                {anyLabel}
              </CommandItem>
              {(filter.options ?? []).map((o) => (
                <CommandItem key={o.value} value={`${o.label} ${o.value}`} onSelect={() => pick(o.value)}>
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
